import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { Link } from "react-router";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import type { MenuProps, StylesConfig } from "react-select";
import { api } from "../../api";
import { useAuth } from "../../hooks/auth";
import {
  IKindredBrandReference,
  IKindredBrandSearchResponse,
} from "../../interfaces";

export interface BrandOption {
  value: string;
  label: string;
}

/** Below this the search doesn't fire — too many matches to be useful. */
const MIN_QUERY_LENGTH = 2;
const PAGE_SIZE = 20;

/** A pasted brand id is looked up directly — name search would never match it. */
const BRAND_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const toOption = (b: IKindredBrandReference): BrandOption => ({
  value: b.id,
  label: `${b.name}${b.isTopBrand ? " ★" : ""}`,
});

/** The ★ is carried on the label so react-select's own filtering and the
 *  selected-value display stay plain strings. */
const formatBrandOption = (opt: BrandOption) => {
  const isTop = opt.label.endsWith(" ★");
  const name = isTop ? opt.label.slice(0, -2) : opt.label;
  return (
    <span>
      {name}
      {isTop && (
        <Badge bg="info" className="ms-1" style={{ fontSize: "0.7em" }}>
          ★ Top Brand
        </Badge>
      )}
    </span>
  );
};

const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
  BRAND_ID_PATTERN.test(inputValue.trim())
    ? "No brand with that ID"
    : inputValue.length < MIN_QUERY_LENGTH
      ? `Type at least ${MIN_QUERY_LENGTH} characters...`
      : "No brands found";

/**
 * Loads brand options, and remembers how many matched in total.
 *
 * The count matters: the search returns the top {PAGE_SIZE} by relevance, and
 * without saying so a truncated list is indistinguishable from "that brand
 * doesn't exist" — which is exactly how a findable brand got reported missing.
 */
const useBrandSearch = () => {
  const { getToken } = useAuth();
  const abortRef = useRef<AbortController | null>(null);
  const [shown, setShown] = useState(0);
  const [total, setTotal] = useState(0);

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: BrandOption[]) => void) => {
      if (!inputValue || inputValue.length < MIN_QUERY_LENGTH) {
        setShown(0);
        setTotal(0);
        callback([]);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const query = inputValue.trim();

      (async () => {
        try {
          const token = await getToken();
          const headers = {
            Authorization: token ? `Bearer ${token}` : undefined,
          };

          if (BRAND_ID_PATTERN.test(query)) {
            const brand = await api.get<IKindredBrandReference>(
              `kindred/reference/brands/${encodeURIComponent(query)}`,
              { headers, signal: controller.signal }
            );
            if (controller.signal.aborted) return;
            const options = brand ? [toOption(brand)] : [];
            setShown(options.length);
            setTotal(options.length);
            callback(options);
            return;
          }

          const data = await api.get<IKindredBrandSearchResponse>(
            `kindred/reference/brands/search?q=${encodeURIComponent(
              query
            )}&limit=${PAGE_SIZE}`,
            { headers, signal: controller.signal }
          );
          // A superseded request must not overwrite the winner's count.
          if (controller.signal.aborted) return;
          setShown(data.results.length);
          setTotal(data.total);
          callback(data.results.map(toOption));
        } catch {
          if (!controller.signal.aborted) {
            setShown(0);
            setTotal(0);
            callback([]);
          }
        }
      })();
    },
    [getToken]
  );

  // Menu footer telling the tester the list was cut short, so "not in the
  // list" reads as "narrow your search" rather than "it isn't there".
  const Menu = useCallback(
    (props: MenuProps<BrandOption, boolean>) => (
      <components.Menu {...props}>
        {props.children}
        {total > shown && shown > 0 && (
          <div
            className="px-3 py-2 border-top text-muted"
            style={{ fontSize: "0.75rem" }}
          >
            Showing the closest {shown} of {total} matches — keep typing to
            narrow it down.
          </div>
        )}
      </components.Menu>
    ),
    [shown, total]
  );

  return { loadOptions, Menu };
};

const compactStyles: StylesConfig<BrandOption, boolean> = {
  control: (base) => ({ ...base, minHeight: 31, fontSize: "0.875rem" }),
};

/** Single-brand picker used on the create/edit forms. */
export const BrandTypeahead: React.FC<{
  brandId: string;
  brandName: string;
  onChange: (brandId: string, brandName: string) => void;
  /** Filters only: lets the brand be cleared, reported as onChange("", ""). */
  clearable?: boolean;
}> = ({ brandId, brandName, onChange, clearable = false }) => {
  const { getToken } = useAuth();
  const { loadOptions, Menu } = useBrandSearch();
  const [selected, setSelected] = useState<BrandOption | null>(
    brandId ? { value: brandId, label: brandName || brandId } : null
  );

  // Resolve the id to a real name/★ so an edit form doesn't show a raw guid.
  useEffect(() => {
    if (!brandId) {
      setSelected(null);
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        const brand = await api.get<IKindredBrandReference>(
          `kindred/reference/brands/${encodeURIComponent(brandId)}`,
          { headers: { Authorization: token ? `Bearer ${token}` : undefined } }
        );
        if (brand) setSelected(toOption(brand));
      } catch {
        /* keep current label */
      }
    })();
  }, [brandId]);

  return (
    <Form.Group className="mb-2">
      <Form.Label className="small mb-0">Brand</Form.Label>
      <AsyncSelect<BrandOption>
        // No cacheOptions: a cache hit skips loadOptions, which would leave
        // the menu footer quoting the previous query's match count.
        defaultOptions={false}
        loadOptions={loadOptions}
        components={{ Menu }}
        value={selected}
        onChange={(opt) => {
          setSelected(opt);
          if (opt) onChange(opt.value, opt.label);
          else if (clearable) onChange("", "");
        }}
        formatOptionLabel={formatBrandOption}
        noOptionsMessage={noOptionsMessage}
        placeholder="Search brand name or paste an ID..."
        isClearable={clearable}
        styles={{
          control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }),
          valueContainer: (base) => ({ ...base, padding: "0 6px" }),
          input: (base) => ({ ...base, margin: 0, padding: 0 }),
          indicatorsContainer: (base) => ({
            ...base,
            "> div": { padding: 4 },
          }),
        }}
      />
      {brandId && (
        <Link
          to={`/kindred/brands/${brandId}`}
          target="_blank"
          className="small text-decoration-none"
        >
          View Brand →
        </Link>
      )}
    </Form.Group>
  );
};

/** Multi-brand picker used as a list filter on coupons / deals / offers. */
export const BrandMultiSelect: React.FC<{
  /** readonly because that is what react-select hands back for a multi value. */
  value: readonly BrandOption[];
  onChange: (options: BrandOption[]) => void;
}> = ({ value, onChange }) => {
  const { loadOptions, Menu } = useBrandSearch();

  return (
    <AsyncSelect<BrandOption, true>
      isMulti
      loadOptions={loadOptions}
      defaultOptions={[]}
      components={{ Menu }}
      value={value}
      onChange={(opts) => onChange([...(opts ?? [])])}
      formatOptionLabel={formatBrandOption}
      placeholder="Type a brand name, or paste a brand ID..."
      noOptionsMessage={noOptionsMessage}
      styles={compactStyles as StylesConfig<BrandOption, true>}
    />
  );
};
